import React, { useState, useEffect } from 'react';
import { FiX, FiSave } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { visaService } from '../../services/api';
import './VisaManagement.css';

const VISA_TYPES = [
  'Skilled Worker',
  'Student',
  'Family',
  'Visitor',
  'Other',
];

const VisaForm = ({ visa, onClose, onSave }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    visaType: visa?.visaType || '',
    visaNumber: visa?.visaNumber || '',
    countryOfOrigin: visa?.countryOfOrigin || '',
    issueDate: visa ? new Date(visa.issueDate).toISOString().split('T')[0] : '',
    expiryDate: visa ? new Date(visa.expiryDate).toISOString().split('T')[0] : '',
    rightToWorkCheckDate: visa
      ? new Date(visa.rightToWorkCheckDate).toISOString().split('T')[0]
      : '',
    notes: visa?.notes || '',
  });

  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!formData.visaType.trim()) {
      newErrors.visaType = 'Visa type is required';
    }

    if (!formData.visaNumber.trim()) {
      newErrors.visaNumber = 'Visa number is required';
    }

    if (!formData.countryOfOrigin.trim()) {
      newErrors.countryOfOrigin = 'Country of origin is required';
    }

    if (!formData.issueDate) {
      newErrors.issueDate = 'Issue date is required';
    }

    if (!formData.expiryDate) {
      newErrors.expiryDate = 'Expiry date is required';
    } else if (new Date(formData.expiryDate) <= new Date(formData.issueDate)) {
      newErrors.expiryDate = 'Expiry date must be after issue date';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const payload = {
        ...formData,
        issueDate: new Date(formData.issueDate).toISOString(),
        expiryDate: new Date(formData.expiryDate).toISOString(),
        rightToWorkCheckDate: formData.rightToWorkCheckDate
          ? new Date(formData.rightToWorkCheckDate).toISOString()
          : null,
      };

      if (visa?.id) {
        // Update existing visa
        await visaService.update(visa.id, payload);
      } else {
        // Create new visa
        await visaService.create(payload);
      }

      onSave();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save visa information');
      console.error('Error saving visa:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{visa ? 'Update Visa Information' : 'Add Visa Information'}</h2>
          <button className="close-btn" onClick={onClose}>
            <FiX size={24} />
          </button>
        </div>

        {error && <div className="error-banner">{error}</div>}

        <form onSubmit={handleSubmit} className="visa-form">
          <div className="form-group">
            <label htmlFor="visaType">Visa Type *</label>
            <select
              id="visaType"
              name="visaType"
              value={formData.visaType}
              onChange={handleChange}
              className={errors.visaType ? 'input-error' : ''}
            >
              <option value="">Select a visa type...</option>
              {VISA_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
            {errors.visaType && <span className="error-message">{errors.visaType}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="visaNumber">Visa Number *</label>
            <input
              id="visaNumber"
              type="text"
              name="visaNumber"
              value={formData.visaNumber}
              onChange={handleChange}
              placeholder="Enter your visa number"
              className={errors.visaNumber ? 'input-error' : ''}
            />
            {errors.visaNumber && <span className="error-message">{errors.visaNumber}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="countryOfOrigin">Country of Origin *</label>
            <input
              id="countryOfOrigin"
              type="text"
              name="countryOfOrigin"
              value={formData.countryOfOrigin}
              onChange={handleChange}
              placeholder="Enter country name"
              className={errors.countryOfOrigin ? 'input-error' : ''}
            />
            {errors.countryOfOrigin && <span className="error-message">{errors.countryOfOrigin}</span>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="issueDate">Issue Date *</label>
              <input
                id="issueDate"
                type="date"
                name="issueDate"
                value={formData.issueDate}
                onChange={handleChange}
                className={errors.issueDate ? 'input-error' : ''}
              />
              {errors.issueDate && <span className="error-message">{errors.issueDate}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="expiryDate">Expiry Date *</label>
              <input
                id="expiryDate"
                type="date"
                name="expiryDate"
                value={formData.expiryDate}
                onChange={handleChange}
                className={errors.expiryDate ? 'input-error' : ''}
              />
              {errors.expiryDate && <span className="error-message">{errors.expiryDate}</span>}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="rightToWorkCheckDate">Right to Work Check Date</label>
            <input
              id="rightToWorkCheckDate"
              type="date"
              name="rightToWorkCheckDate"
              value={formData.rightToWorkCheckDate}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="notes">Notes</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Add any additional notes..."
              rows="3"
            />
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? (
                <>
                  <div className="inline-spinner"></div> Saving...
                </>
              ) : (
                <>
                  <FiSave /> Save Visa Information
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VisaForm;
